import type { ShareSocialItemType } from './ShareProgress.types';
import type { ReactElement } from 'react';

import { Box, Button, Card, Flex, Heading, Separator, Text } from '@rango-dev/whale';
import { Share2 } from 'lucide-react';
import React, { Fragment } from 'react';
import { useTranslation } from 'react-i18next';

import { useAppSelector } from '../../../hooks/reduxHooks';
import { TelegramIcon, XIcon } from '../../icons';

import { getProgressData, getSocialProgressText } from './ProfileProgress.helpers';

const BRAND_ICON_SIZE = '32';
const SHARE_ICON_SIZE = 16;

function ShareSocialItem(props: ShareSocialItemType): ReactElement {
  const { description, icon, link, title, id } = props;
  const { t } = useTranslation();

  return (
    <Card size="1" variant="ghost">
      <Flex justify="between" align="center" width="100%" gap="3">
        <Flex gap="4" align="center" flexGrow="1">
          {icon}
          <Flex direction="column">
            <Heading size="3" weight="bold">
              {title}
            </Heading>
            <Box display={{ initial: 'none', sm: 'block' }}>
              <Text size="2" color="gray">
                {description}
              </Text>
            </Box>
          </Flex>
        </Flex>
        <Button
          id={`profile-share-modal-${id}-btn`}
          asChild
          variant="outline"
          color="cyan"
          size="2"
          radius="full">
          <a href={link} target="_blank" rel="noreferrer">
            <Share2 size={SHARE_ICON_SIZE} />
            {t('Share')}
          </a>
        </Button>
      </Flex>
    </Card>
  );
}

export function ShareProgressSocial(): ReactElement {
  const { t } = useTranslation();
  const profileSummary = useAppSelector((state) => state.profile.profileSummary.private.data);
  const progressData = getProgressData(profileSummary);
  const socialText = getSocialProgressText(progressData);

  const encodedLink = encodeURIComponent(
    `https://app.rango.exchange/profile/${profileSummary?.username}`,
  );

  const socialItems: ShareSocialItemType[] = [
    {
      title: 'X (Twitter)',
      id: 'twitter',
      description: t('Share your progress on {{socialName}}', { socialName: 'X' }),
      link: `https://twitter.com/intent/tweet?url=${encodedLink}&text=${encodeURIComponent(
        socialText,
      )}`,
      icon: <XIcon size={BRAND_ICON_SIZE} />,
    },
    {
      title: 'Telegram',
      id: 'telegram',
      description: t('Share your progress on {{socialName}}', { socialName: 'Telegram' }),
      link: `https://t.me/share/url?text=${encodeURIComponent(socialText)}&url=${encodedLink}`,
      icon: <TelegramIcon size={BRAND_ICON_SIZE} />,
    },
  ];

  return (
    <Box width="100%">
      <Heading size="1" weight="medium" color="gray" mb="2" align="left">
        {t('Share on')}
      </Heading>
      <Flex direction="column" gap="2">
        {socialItems.map((socialItem, index) => (
          <Fragment key={socialItem.id}>
            <ShareSocialItem {...socialItem} />
            {socialItems.length - 1 !== index && <Separator size="4" />}
          </Fragment>
        ))}
      </Flex>
    </Box>
  );
}
